import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export interface SeasonalRuleProduct {
  product_id: StockyPublicBigIntId;
  title: string | null;
  applies: boolean;
  /** "ai" | "heuristic" | "merchant"。merchant は人が直接決めた行。 */
  source: string;
  /**
   * 🔴 true＝この商品は発注点を**手動固定**しているので、適用しても発注数量が動かない。
   * 手動固定は「発注点 − 在庫 − 入荷予定」の引き算だけで数量を決め、需要を一度も
   * 見ない＝需要に掛かる季節係数は届かない（2026-08-02 に実機で踏んだ）。
   */
  reorder_point_is_manual: boolean;
  /**
   * 🔴 true＝**発行済みの注文書**（発注済み／一部入荷）に載っている商品。
   * その注文書の数量はもう確定していて、いま適用しても1つも変わらない
   * （効くのは次に作るドラフトから）。ドラフトは開き直せば計算し直されるので
   * 含まない＝「もう変わらない」と言えるのは発行済みだけ。
   */
  on_placed_purchase_order: boolean;
}

export interface SeasonalRuleEvent {
  slug: string;
  name: string;
  multiplier: number;
  /** AI の提案（まだ係数に効かない）。 */
  proposed: SeasonalRuleProduct[];
  /** マーチャントが判断済み（これだけが係数に効く）。 */
  decided: SeasonalRuleProduct[];
  /** 後方互換。fail-closed 化後は常に false。 */
  is_store_wide: boolean;
  /** merchant がこの国・イベントの対象を一度でも明示確定したか。 */
  is_reviewed: boolean;
  /** 現在または次に来る適用期間（ISO 日付）。 */
  window_start: string;
  window_end: string;
  /**
   * 🔴 今日が適用期間の中か。係数は期間の外では掛からない（1.0）＝
   * これを見ずに「いま適用中」と言うと、8月に年末年始が効いているように読める嘘になる。
   */
  is_active_now: boolean;
}

export async function listSeasonalRules(): Promise<{
  country_code: string | null;
  events: SeasonalRuleEvent[];
}> {
  const body = await apiFetch<{
    country_code: string | null;
    events: SeasonalRuleEvent[];
  }>("/api/seasonal-rules");
  return {
    ...body,
    events: body.events.map((event) => ({
      ...event,
      proposed: event.proposed.map((product) => ({
        ...product,
        product_id: canonicalStockyPublicBigIntId(product.product_id),
      })),
      decided: event.decided.map((product) => ({
        ...product,
        product_id: canonicalStockyPublicBigIntId(product.product_id),
      })),
    })),
  };
}

export function createSeasonalProposals(): Promise<{ created: number }> {
  return apiFetch("/api/seasonal-rules/proposals", { method: "POST" });
}

export function decideSeasonalRules(
  eventSlug: string,
  items: { product_id: StockyPublicBigIntId; applies: boolean }[],
): Promise<{ decided: number }> {
  return apiFetch("/api/seasonal-rules/decide", {
    method: "POST",
    body: {
      event_slug: eventSlug,
      items: items.map((item) => ({
        ...item,
        product_id: canonicalStockyPublicBigIntId(item.product_id),
      })),
    },
  });
}
